// React Query hooks for messages

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { messagesApi } from '@/lib/api';
import type { Message, SendMessageRequest } from '@/types';

// Query key factory for consistent cache management
export const messageKeys = {
  all: ['messages'] as const,
  list: () => [...messageKeys.all, 'list'] as const,
};

/**
 * Hook to fetch messages with polling for real-time updates
 */
export function useMessages(limit: number = 50) {
  return useQuery<Message[], Error>({
    queryKey: messageKeys.list(),
    queryFn: () => messagesApi.list(limit),
    refetchInterval: 5000, // Poll every 5 seconds for live updates
    staleTime: 1000, // Consider stale after 1 second
  });
}

/**
 * Hook to send a new message
 */
export function useSendMessage() {
  const queryClient = useQueryClient();

  return useMutation<Message, Error, SendMessageRequest>({
    mutationFn: (data) => messagesApi.send(data),
    onSuccess: (newMessage) => {
      // Optimistically add the new message to the cache
      queryClient.setQueryData<Message[]>(messageKeys.list(), (oldMessages) => {
        if (!oldMessages) return [newMessage];
        return [...oldMessages, newMessage];
      });
      // Then invalidate to get the latest from server
      queryClient.invalidateQueries({ queryKey: messageKeys.list() });
    },
  });
}

/**
 * Hook to delete a message
 */
export function useDeleteMessage() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: (messageId) => messagesApi.delete(messageId),
    onSuccess: (_, deletedId) => {
      // Optimistically remove the message from cache
      queryClient.setQueryData<Message[]>(messageKeys.list(), (oldMessages) => {
        if (!oldMessages) return [];
        return oldMessages.filter((m) => m.id !== deletedId);
      });
      queryClient.invalidateQueries({ queryKey: messageKeys.list() });
    },
  });
}
